import { useContext } from "react";

import { appDataContext } from "@/context";
import { Link } from "react-router-dom";
import styled from "styled-components";

import Icon from "@/utils/Icon";

const Footer = () => {
  // Use a fixed date to prevent hydration mismatch (server date vs client date)
  // or use useEffect to set it after mount, but for footer year, 2026 is fine if hardcoded or handled safely.
  // Using 'new Date().getFullYear()' on the server and client at exactly midnight Jan 1st can differ, but rare.
  // More important is if 'lang' or 'routeMap' differs initially.
  
  // However, the error "Hydration failed because the initial UI does not match" often comes from
  // conditional rendering like {lang?....} if lang is undefined initially on client but populated on server (or vice versa).
  // But here, dataContext comes from React Context.

  const dataContext = useContext(appDataContext);
  const lang = dataContext?.lang;
  const routeMap = dataContext?.routeMap;
  
  // Safe year rendering
  const currentYear = new Date().getFullYear(); 

  const openCookyPolicy = () => {
    window.dispatchEvent(
      new CustomEvent("displayCookie", {
        open: true,
      })
    );
  };

  const funcs = { openCookyPolicy };

  return (
    <Wrapper suppressHydrationWarning>
      <Container>
        <Items>
          {lang?.footer?.links?.map((link, id) => (
            <ListBlock key={`footerlinks_${id}`}>
              <BlockTitle>{link.title}</BlockTitle>
              <ItemsLink>
                {link?.items?.map((item, i) => (
                  <li key={`footerLInks_${i}`}>
                    {item?.callFunction ? (
                      <Link onClick={() => funcs[item.callFunction]()}>
                        {item.label}
                      </Link>
                    ) : (
                      <Link to={`${routeMap?.urls[item.url] ?? item.url}`}>
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ItemsLink>
            </ListBlock>
          ))}
          <ListBlockSocial>
            <BlockTitle>{lang?.footer?.socialLinks?.title}</BlockTitle>
            <ItemsLink>
              {lang?.footer?.socialLinks?.items?.map((item, i) => (
                <SocialListItem key={`footerLInks_${i}`}>
                  {item?.callFunction ? (
                    <Link onClick={() => funcs[item.callFunction]()}>
                      {item.label}
                    </Link>
                  ) : (
                    <>
                      <Icon
                        icon={item.icon}
                        size={24}
                        color="white"
                        width={24}
                        height={24}
                      />
                      <Link to={`${routeMap?.urls[item.url] ?? item.url}`}>
                        {item.label}
                      </Link>
                    </>
                  )}
                </SocialListItem>
              ))}
            </ItemsLink>
          </ListBlockSocial>
        </Items>
      </Container>
      <Copyright>
        <p>
          © Copyright 2005 - {currentYear} | {lang?.footer.copyright}
          <Link
            to={`${routeMap?.urls[lang?.footer.url] ?? lang?.footer.url}`}
            state={{ scrollToTop: true }}
          >
            {lang?.footer.label}
          </Link>
        </p>
      </Copyright>
    </Wrapper>
  );
};

export default Footer;

const Container = styled.div.attrs({
  className: "Container",
})`
  @media screen and (min-width: 768px) {
    width: 1400px;
    max-width: 100%;
    margin: 0 auto;
  }
`;

const Items = styled.nav.attrs({
  className: "Items",
})`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  margin: 0 auto;

  justify-content: flex-start;
  align-items: flex-start;
  color: white;
  justify-content: center;
  align-items: center;
  * {
    text-align: center;
  }

  @media screen and (min-width: 670px) {
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 32px;
    * {
      text-align: left;
    }
  }
`;

const ItemsLink = styled.ul.attrs({
  className: "ItemsLink",
})`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  gap: 6px;
  padding: 10px 10px 10px 0;
  justify-content: center;
  align-items: center;
  @media screen and (min-width: 670px) {
    justify-content: flex-start;
    align-items: flex-start;
  }
  flex-direction: column;
  * {
    color: white;
  }
  flex: 1 1 100%;
  li a {
    text-decoration: none;
    &:hover {
      text-shadow: 0 0 5px rgba(255, 255, 255, 0.5);
    }
  }
`;

const Copyright = styled.div.attrs({
  className: "Copyright",
})`
  background-color: ${(props) => props.theme.colors.blueDark};
  font-size: 1.2rem;
  padding-bottom: 1rem;
  padding-top: 1rem;
  text-align: center;
  p {
    color: rgba(255, 255, 255, 0.9);
  }
  a {
    color: rgba(255, 255, 255, 0.9);
    text-decoration: underline;
  }
  a:visited {
    color: rgba(255, 255, 255, 0.9);
  }
`;

const Wrapper = styled.footer.attrs({
  className: "Wrapper",
})`
  background-color: ${(props) => props.theme.colors.blue};
  margin-top: auto;
  width: 100%;
  nav {
    color: white;
  }
`;

const BlockTitle = styled.h3.attrs({
  className: "BlockTitle",
})`
  margin-bottom: 8px;
  color: white;
  font-size: 1.6rem;
  font-family: ${(props) => props.theme.fonts.body};
  text-transform: uppercase;
  font-weight: 600;
  &::after {
    content: "";
    display: block;
    width: 100%;
    height: 2px;
    background-color: ${(props) => props.theme.colors.white};
    margin-top: 8px;
    @media screen and (min-width: 670px) {
      width: 70px;
    }
  }
`;

const ListBlock = styled.div.attrs({
  className: "ListBlock",
})`
  flex: 0 1 300%;
  width: 100%;
  @media screen and (min-width: 670px) {
    max-width: 33%;
    flex: 0 1 25%;
    width: 100%;
  }
  a:hover {
    text-decoration: underline;
  }
`;
const ListBlockSocial = styled.div.attrs({
  className: "ListBlockSocial",
})`
  flex: 0 1 300%;
  width: 100%;
  margin-left: auto;
  @media screen and (min-width: 670px) {
    max-width: 33%;
    flex: 0 1 25%;
    width: 100%;
  }
  a:hover {
    text-decoration: underline;
  }
`;

const SocialListItem = styled.li.attrs({
  className: "SocialListItem",
})`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-decoration: none;
  gap: 10px;
`;
